import { ISkill } from "@/api/skills";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNowStrict } from "date-fns";
import { DeleteSkillButton } from "./DeleteSkillButton";

export const columns: ColumnDef<ISkill>[] = [
    {
        id: "select",
        enableSorting: false,
        // Matches the document library's checkbox column exactly: `w-12`
        // pins the width (see ColumnMeta.cellClassName in data-table.tsx for
        // why this table relies on width classes instead of react-table's
        // numeric `size`), and `p-0` swaps out the default TableCell `p-2`
        // so the wrapper below can supply its own `px-3` to center the
        // checkbox.
        meta: { cellClassName: "w-12 p-0" },
        cell: ({ row }) => (
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    row.toggleSelected(!row.getIsSelected());
                }}
                className="flex h-full items-center justify-center px-3"
            >
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(checked) => row.toggleSelected(!!checked)}
                    aria-label="Select row"
                    className="pointer-events-none"
                />
            </div>
        ),
    },
    {
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        // Auto layout (see data-table.tsx) sizes columns to content, so this
        // is what actually makes long names truncate instead of stretching
        // the table, and keeps Name as the column that absorbs the table's
        // leftover width — same technique as the document library's Name
        // column.
        meta: { cellClassName: "min-w-[200px] max-w-[300px]" },
        cell: ({ row }) => {
            const name = row.getValue("name") as string;
            const id = row.original.id;
            const isCore = row.original.is_core;
            return (
                <div className="flex min-w-0 items-center gap-2">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger className="block max-w-full min-w-0 truncate text-left font-medium">
                                <LinkRouter to={`/admin/skills/${id}/details`}>
                                    {name}
                                </LinkRouter>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{name}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    {isCore && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <Badge
                                            variant="secondary"
                                            className="border-brand-copper/30 bg-brand-copper/10 text-brand-copper shrink-0"
                                        >
                                            Core
                                        </Badge>
                                    }
                                />
                                <TooltipContent>
                                    <p>
                                        Loaded into every chat in this org, with
                                        or without an agent, above
                                        agent-required and contextual skills.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            );
        },
    },
    {
        id: "scope",
        header: "Scope",
        enableSorting: false,
        cell: ({ row }) => {
            const org = row.original.organization;
            if (org) {
                return <Badge variant="secondary">{org.name}</Badge>;
            }
            const group = row.original.organization_group;
            if (group) {
                return <Badge variant="secondary">{group.name}</Badge>;
            }
            return (
                <span className="text-muted-foreground text-xs">Global</span>
            );
        },
    },
    {
        id: "status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => {
            const isPublished = row.original.status === "active";
            if (isPublished) {
                return (
                    <Badge className="border-emerald-200 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/10">
                        Published
                    </Badge>
                );
            }
            return <Badge variant="secondary">Draft</Badge>;
        },
    },
    {
        accessorKey: "updated_at",
        header: "Last Updated",
        enableSorting: true,
        cell: ({ row }) => {
            const date = row.getValue("updated_at") as string;
            return (
                <span
                    className="text-muted-foreground text-sm"
                    title={new Date(date).toLocaleString()}
                >
                    {formatDistanceToNowStrict(date, { addSuffix: true })}
                </span>
            );
        },
    },
    {
        id: "actions",
        enableSorting: false,
        cell: ({ row }) => (
            <DeleteSkillButton
                skillId={row.original.id}
                status={row.original.status}
            />
        ),
    },
];
